/**
 * BatchProgressSheet Component
 *
 * Displays live batch operation progress in a mobile-friendly bottom sheet.
 * Features auto-scrolling results list, cursor-based pagination for incremental fetching,
 * and action buttons for pause/resume/cancel operations.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  ExternalLink,
  ChevronRight,
  Copy,
  Check,
  Play,
  Pause,
  X,
  Loader2,
} from 'lucide-react';

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import {
  useBatchOperation,
  useBatchOperationProgress,
  usePauseBatchOperation,
  useResumeBatchOperation,
  useCancelBatchOperation,
  type BatchLocationResult,
  type BatchOperationStatus,
} from '@/api/batch-operations';

interface BatchProgressSheetProps {
  operationId: string | number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const OPERATION_TYPE_LABELS: Record<string, string> = {
  auto_enrich: 'Auto-Enrich',
  batch_tags: 'Tag Analysis',
  batch_publish: 'Batch Publish',
  bulk_update: 'Bulk Update',
  ai_content: 'AI Content',
  batch_classify_type: 'Classify type, styles & features',
  batch_resync_content: 'Re-sync AI Content',
  account_disable: 'Disable Account',
};

interface StatusBadgeInfo {
  variant: 'default' | 'secondary' | 'destructive' | 'outline' | 'warning';
  label: string;
}

const STATUS_BADGES: Record<BatchOperationStatus, StatusBadgeInfo> = {
  pending: { variant: 'secondary', label: 'Pending' },
  running: { variant: 'default', label: 'Running' },
  paused: { variant: 'warning', label: 'Paused' },
  completed: { variant: 'secondary', label: 'Completed' },
  failed: { variant: 'destructive', label: 'Failed' },
  cancelled: { variant: 'outline', label: 'Cancelled' },
};

const RESULT_STATUS_ICONS: Record<string, typeof CheckCircle> = {
  success: CheckCircle,
  failed: XCircle,
  skipped: AlertTriangle,
};

const RESULT_STATUS_COLORS: Record<string, string> = {
  success: 'text-green-500',
  failed: 'text-red-500',
  skipped: 'text-yellow-500',
};

interface ProgressResultRowProps {
  result: BatchLocationResult;
  isExpanded: boolean;
  onToggle: () => void;
}

function ProgressResultRow({ result, isExpanded, onToggle }: ProgressResultRowProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const StatusIcon = RESULT_STATUS_ICONS[result.status] ?? AlertTriangle;
  const statusColor = RESULT_STATUS_COLORS[result.status] ?? 'text-muted-foreground';
  const hasMessage = result.message != null && result.message.length > 0;
  const isFailed = result.status === 'failed';

  const handleCopy = async (): Promise<void> => {
    if (result.message) {
      await navigator.clipboard.writeText(result.message);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 2000);
    }
  };

  return (
    <div
      className="border-b last:border-b-0"
      data-testid={`batch-progress-result-row-${result.locationId}`}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={!isFailed || !hasMessage}
        className={cn(
          'w-full flex items-center gap-3 p-3 text-left transition-colors',
          isFailed && hasMessage && 'hover:bg-muted/50 cursor-pointer',
          (!isFailed || !hasMessage) && 'cursor-default'
        )}
      >
        {isFailed && hasMessage && (
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform shrink-0',
              isExpanded && 'rotate-90'
            )}
          />
        )}
        {(!isFailed || !hasMessage) && <div className="w-4" />}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/synced-locations/${result.locationId}`}
              className="text-primary hover:underline truncate"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {result.locationName || `Location #${result.locationId}`}
              <ExternalLink className="inline h-3 w-3 ml-1" />
            </Link>
          </div>
          {!isExpanded && hasMessage && !isFailed && (
            <p className="text-sm text-muted-foreground truncate mt-0.5">
              {result.message}
            </p>
          )}
        </div>

        <StatusIcon
          className={cn(
            'h-4 w-4 shrink-0',
            statusColor
          )}
        />
      </button>

      {isExpanded && isFailed && hasMessage && (
        <div className="px-3 pb-3 pl-10">
          <div className="relative">
            <pre className="text-sm bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words font-mono text-red-600">
              {result.message}
            </pre>
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 h-7 w-7 p-0"
              onClick={(e) => {
                e.stopPropagation();
                void handleCopy();
              }}
              data-testid={`batch-progress-copy-error-${result.locationId}`}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function BatchProgressSheet({
  operationId,
  open,
  onOpenChange,
}: BatchProgressSheetProps): JSX.Element {
  const [since, setSince] = useState<string | undefined>(undefined);
  const [accumulatedResults, setAccumulatedResults] = useState<BatchLocationResult[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [autoScroll, setAutoScroll] = useState(true);
  const resultsEndRef = useRef<HTMLDivElement>(null);

  // Reset state when sheet opens with a new operation
  useEffect(() => {
    if (open) {
      setSince(undefined);
      setAccumulatedResults([]);
      setExpandedIds(new Set());
      setAutoScroll(true);
    }
  }, [open, operationId]);

  // Fetch operation details for header info (type, partner account name)
  const { data: operationData } = useBatchOperation(String(operationId), { enabled: open });

  // Fetch progress data with cursor-based pagination
  const {
    data: progressData,
    isLoading,
    isError: progressFailed,
    refetch: refetchProgress,
    isFetching: progressFetching,
  } = useBatchOperationProgress(
    operationId,
    { since, limit: 50 },
    { enabled: open }
  );

  // Mutations for actions
  const pauseMutation = usePauseBatchOperation();
  const resumeMutation = useResumeBatchOperation();
  const cancelMutation = useCancelBatchOperation();

  // Accumulate results and update cursor
  useEffect(() => {
    if (progressData?.results && progressData.results.length > 0) {
      setAccumulatedResults((prev) => {
        // Create a map of existing results by locationId for deduplication
        const existingMap = new Map(prev.map((r) => [r.locationId, r]));

        // Add or update results
        for (const result of progressData.results) {
          existingMap.set(result.locationId, result);
        }

        return Array.from(existingMap.values());
      });

      // Update cursor for next fetch if there's more data
      if (progressData.nextCursor && progressData.hasMore) {
        setSince(progressData.nextCursor);
      }
    }
  }, [progressData]);

  // Auto-scroll to bottom when new results arrive
  useEffect(() => {
    if (autoScroll && resultsEndRef.current) {
      resultsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [accumulatedResults, autoScroll]);

  const toggleExpanded = useCallback((locationId: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) {
        next.delete(locationId);
      } else {
        next.add(locationId);
      }
      return next;
    });
  }, []);

  const handlePause = useCallback((): void => {
    pauseMutation.mutate(String(operationId));
  }, [operationId, pauseMutation]);

  const handleResume = useCallback((): void => {
    resumeMutation.mutate(String(operationId));
  }, [operationId, resumeMutation]);

  const handleCancel = useCallback((): void => {
    cancelMutation.mutate(String(operationId));
  }, [operationId, cancelMutation]);

  // Compute progress values
  const totalCount = progressData?.totalCount ?? 0;
  const processedCount = progressData?.processedCount ?? 0;
  const successCount = progressData?.successCount ?? 0;
  const failedCount = progressData?.failedCount ?? 0;
  const skippedCount = progressData?.skippedCount ?? 0;
  const status = progressData?.status ?? 'pending';

  const progressPercent = totalCount > 0
    ? Math.round((processedCount / totalCount) * 100)
    : 0;

  const statusBadge = STATUS_BADGES[status];
  const isRunning = status === 'running';
  const isPaused = status === 'paused';
  const isActive = isRunning || isPaused || status === 'pending';

  // Get operation type label from full operation data
  const operationType = operationData?.operationType ?? '';
  const operationTypeLabel = OPERATION_TYPE_LABELS[operationType] ?? 'Batch Operation';
  const partnerAccountName = operationData?.partnerAccountName ?? operationData?.partnerAccountId;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]" data-testid="batch-progress-sheet">
        <DrawerHeader data-testid="batch-progress-header">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <DrawerTitle>
                {operationTypeLabel}
              </DrawerTitle>
              <DrawerDescription>
                {partnerAccountName ? `${partnerAccountName} - Live progress` : 'Live progress view'}
              </DrawerDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={statusBadge.variant} data-testid="batch-progress-status">
                {statusBadge.label}
              </Badge>
            </div>
          </div>

          {/* Action buttons */}
          {isActive && (
            <div className="flex gap-2 mt-4">
              {isRunning && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePause}
                  disabled={pauseMutation.isPending}
                  data-testid="batch-progress-pause-btn"
                >
                  <Pause className="mr-1 h-4 w-4" />
                  {pauseMutation.isPending ? 'Pausing...' : 'Pause'}
                </Button>
              )}

              {isPaused && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleResume}
                  disabled={resumeMutation.isPending}
                  data-testid="batch-progress-resume-btn"
                >
                  <Play className="mr-1 h-4 w-4" />
                  {resumeMutation.isPending ? 'Resuming...' : 'Resume'}
                </Button>
              )}

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cancelMutation.isPending}
                    data-testid="batch-progress-cancel-btn"
                  >
                    <X className="mr-1 h-4 w-4" />
                    {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel Batch Operation?</AlertDialogTitle>
                    <AlertDialogDescription data-testid="batch-progress-cancel-description">
                      {operationType === 'account_disable'
                        ? 'This stops the disable and removes its pending jobs. The account stays enabled; ' +
                          'locations already unpublished stay unpublished. Run Disable again to finish it.'
                        : 'This will stop the batch operation and remove all pending jobs. ' +
                          'Locations already processed will keep their changes.'}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Running</AlertDialogCancel>
                    <AlertDialogAction onClick={handleCancel}>
                      Cancel Operation
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </DrawerHeader>

        {/* Progress section */}
        <div className="px-4 pb-4 space-y-4">
          {progressFailed && (
            <div
              role="alert"
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
              data-testid="batch-progress-error"
            >
              <span>Could not load the progress of this operation. The numbers below may be out of date.</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { void refetchProgress(); }}
                disabled={progressFetching}
                data-testid="batch-progress-retry-btn"
              >
                {progressFetching ? 'Retrying…' : 'Retry'}
              </Button>
            </div>
          )}
          <div className="space-y-2">
            <Progress
              value={progressPercent}
              className="h-3"
              data-testid="batch-progress-bar"
            />
            <div
              className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm"
              data-testid="batch-progress-counts"
            >
              <span className="font-medium">
                Processed {String(processedCount)} of {String(totalCount)} ({String(progressPercent)}%)
              </span>
              <span className="text-green-600">{String(successCount)} success</span>
              <span className="text-red-600">{String(failedCount)} failed</span>
              <span className="text-yellow-600">{String(skippedCount)} skipped</span>
            </div>
          </div>

          {/* Auto-scroll toggle */}
          {accumulatedResults.length > 5 && (
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setAutoScroll(!autoScroll); }}
                className="text-xs"
              >
                {autoScroll ? 'Disable auto-scroll' : 'Enable auto-scroll'}
              </Button>
            </div>
          )}
        </div>

        {/* Results list */}
        <div
          className="flex-1 overflow-auto px-4 pb-4"
          data-testid="batch-progress-results-list"
        >
          {isLoading && accumulatedResults.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading...</span>
            </div>
          ) : accumulatedResults.length === 0 ? (
            progressFailed ? null : (
              <div className="text-center py-8 text-muted-foreground">
                Waiting for results...
              </div>
            )
          ) : (
            <div className="border rounded-md">
              {accumulatedResults.map((result) => (
                <ProgressResultRow
                  key={result.locationId}
                  result={result}
                  isExpanded={expandedIds.has(result.locationId)}
                  onToggle={() => { toggleExpanded(result.locationId); }}
                />
              ))}
              <div ref={resultsEndRef} />
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
